import React, { useState, useEffect } from 'react'
import { WizardState, ExamOption, SyllabusData } from '../Index'
import { buildDefaultTestTitle, isAutoGeneratedTitle } from '../titleHelper'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  GraduationCap, BookOpen, Layers, FileText, ChevronDown, ChevronRight,
  Loader2, CheckSquare, Square, Info, Database, AlertCircle
} from 'lucide-react'

interface Props {
  state: WizardState
  updateState: (patch: Partial<WizardState>) => void
  exams: ExamOption[]
}

export default function StepExamSyllabus({ state, updateState, exams }: Props) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expandedSubjects, setExpandedSubjects] = useState<Record<number, boolean>>({})
  const [expandedChapters, setExpandedChapters] = useState<Record<number, boolean>>({})

  const loadSyllabiForExam = async (examId: number, isExplicitChange: boolean = false) => {
    setLoading(true)
    setError(null)

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
      const res = await fetch(`/admin/test_generator/load_syllabi?exam_id=${examId}`, {
        headers: {
          'X-CSRF-Token': token,
          'Accept': 'application/json'
        }
      })

      if (!res.ok) {
        throw new Error(`Failed to load syllabi (Status ${res.status})`)
      }

      const data: SyllabusData[] = await res.json()
      const validSyllabiIds = data.map(s => s.id)
      
      const filteredExisting = state.selectedSyllabiIds.filter(id => validSyllabiIds.includes(id))
      const newSelectedSyllabiIds = (!isExplicitChange && filteredExisting.length > 0)
        ? filteredExisting
        : validSyllabiIds

      updateState({
        syllabiData: data,
        selectedSyllabiIds: newSelectedSyllabiIds
      })

      // Expand all subjects by default for convenience
      const initialSubjExpand: Record<number, boolean> = {}
      data.forEach(syl => {
        syl.subjects.forEach(sub => {
          initialSubjExpand[sub.id] = true
        })
      })
      setExpandedSubjects(initialSubjExpand)
    } catch (err: any) {
      setError(err.message || 'An error occurred while loading syllabi.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (state.exam_id && state.syllabiData.length === 0 && !loading) {
      loadSyllabiForExam(state.exam_id, false)
    }
  }, [state.exam_id, state.syllabiData.length])

  const handleExamChange = (examIdStr: string) => {
    if (!examIdStr || examIdStr === '__none__') {
      updateState({ exam_id: null, syllabiData: [], selectedSyllabiIds: [] })
      return
    }

    const examId = parseInt(examIdStr, 10)
    const selectedExamObj = exams.find(e => e.id === examId)
    
    // Check if exam has configured ExamTestTypes matching the current testType or exam_test_type_id
    const dbTestTypes = selectedExamObj?.test_types || []
    const matchedTestType =
      (state.exam_test_type_id ? dbTestTypes.find(tt => tt.id === state.exam_test_type_id) : null) ||
      dbTestTypes.find(tt => tt.test_type_key === state.testType) ||
      dbTestTypes[0]
    const dbSections = matchedTestType?.sections || []
    const dbRules = selectedExamObj?.marking_rules || []
    const primaryRule = dbRules[0]

    const totalFromDbSections = dbSections.reduce((sum, s) => sum + s.total_questions, 0)
    const totalQ = matchedTestType?.total_questions || (totalFromDbSections > 0 ? totalFromDbSections : (state.totalQuestions || 100))
    const posMarks = matchedTestType?.positive_marks ?? primaryRule?.scoring_params?.correct ?? primaryRule?.scoring_params?.full_correct ?? (state.markingScheme?.positive_marks || 1.0)
    const rawWrong = matchedTestType ? matchedTestType.negative_marks : (primaryRule?.scoring_params?.wrong ?? primaryRule?.scoring_params?.any_wrong ?? (primaryRule?.scoring_strategy === 'no_penalty' ? 0 : -0.25))
    const negMarks = Math.abs(Number(rawWrong) || 0)
    const negEnabled = matchedTestType ? (matchedTestType.negative_marks > 0) : (primaryRule?.scoring_strategy !== 'no_penalty' && negMarks > 0)

    const examTitle = buildDefaultTestTitle({
      examName: selectedExamObj?.name,
      examCode: selectedExamObj?.code,
      testTypeName: matchedTestType?.name,
      testTypeKey: matchedTestType?.test_type_key
    })

    const initialSections = dbSections.map(s => ({ ...s, _selected: true }))
    const durationFromDbSections = dbSections.reduce((sum, s) => sum + (s.duration_minutes || 0), 0)
    const isUntimedExamType = matchedTestType && (matchedTestType.duration_minutes === null || matchedTestType.test_type_key === 'practice')
    const durationMins = isUntimedExamType
      ? null
      : (matchedTestType?.duration_minutes ?? (durationFromDbSections > 0 ? durationFromDbSections : (state.durationMinutes || 60)))
    const examTimerType = isUntimedExamType
      ? 'none'
      : (matchedTestType?.timer_type || selectedExamObj?.config?.timer_type || (dbSections.some(s => s.duration_minutes) ? 'sectional' : 'global'))

    const activeMarkingRule = matchedTestType?.marking_rule || dbRules.find(r => r.id === matchedTestType?.exam_marking_rule_id) || primaryRule

    updateState({
      exam_id: examId,
      testType: matchedTestType?.test_type_key || state.testType || 'full_mock',
      exam_test_type_id: matchedTestType?.id || null,
      selectedSyllabiIds: [],
      syllabiData: [],
      blueprintSections: initialSections,
      title: (!state.title || isAutoGeneratedTitle(state.title, selectedExamObj?.name, selectedExamObj?.code)) ? examTitle : state.title,
      totalQuestions: totalQ,
      durationMinutes: durationMins,
      noDuration: Boolean(isUntimedExamType),
      timerType: examTimerType,
      markedForReviewRule: selectedExamObj?.config?.marked_for_review_rule || 'counts_if_answered',
      calculatorEnabled: selectedExamObj?.config?.calculator_enabled || false,
      preTestInstructionsHtml: (matchedTestType as any)?.instructions_html || '',
      totalMarks: totalQ * posMarks,
      markingScheme: {
        selected_rule_id: activeMarkingRule?.id || (matchedTestType?.exam_marking_rule_id ? matchedTestType.exam_marking_rule_id : 'custom'),
        rule_name: activeMarkingRule?.name || matchedTestType?.name || 'Custom Marking Scheme',
        positive_marks: posMarks,
        negative_marks: negMarks,
        negative_marking_enabled: negEnabled,
        scoring_strategy: activeMarkingRule?.scoring_strategy || 'standard',
        scoring_params: activeMarkingRule?.scoring_params || { correct: posMarks, wrong: -negMarks }
      }
    })

    if (state.testType === 'full_mock' && dbSections.length === 0) {
      fetch(`/api/v1/exams/${examId}/mock_test_blueprint`)
        .then(r => r.json())
        .then(blueprint => {
          if (blueprint.sections && blueprint.sections.length > 0) {
            updateState({
              blueprintSections: blueprint.sections.map((s: any) => ({ ...s, _selected: true })),
              totalQuestions: blueprint.total_questions || totalQ,
              durationMinutes: blueprint.duration_minutes || durationMins,
              totalMarks: (blueprint.total_questions || totalQ) * posMarks
            })
          }
        })
        .catch(() => {})
    }

    loadSyllabiForExam(examId, true)
  }

  const toggleSyllabus = (sylId: number) => {
    const isSelected = state.selectedSyllabiIds.includes(sylId)
    const newSelected = isSelected
      ? state.selectedSyllabiIds.filter(id => id !== sylId)
      : [...state.selectedSyllabiIds, sylId]
    updateState({ selectedSyllabiIds: newSelected })
  }

  const toggleAllSyllabi = () => {
    if (state.selectedSyllabiIds.length === state.syllabiData.length) {
      updateState({ selectedSyllabiIds: [] })
    } else {
      updateState({ selectedSyllabiIds: state.syllabiData.map(s => s.id) })
    }
  }

  const toggleSubject = (subId: number) => {
    setExpandedSubjects(prev => ({ ...prev, [subId]: !prev[subId] }))
  }

  const toggleChapter = (chapId: number) => {
    setExpandedChapters(prev => ({ ...prev, [chapId]: !prev[chapId] }))
  }

  const selectedSyllabi = state.syllabiData.filter(s => state.selectedSyllabiIds.includes(s.id))
  const totalReadyQuestions = selectedSyllabi.reduce((sum, s) => sum + (s.total_questions || 0), 0)
  const totalExcludedQuestions = selectedSyllabi.reduce((sum, s) => sum + (s.excluded_count || 0), 0)

  return (
    <div className="space-y-6">
      {/* 1. Exam Selection Header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="exam-select" className="text-base font-bold flex items-center gap-2 text-foreground">
            <GraduationCap className="h-5 w-5 text-primary" />
            Target Exam
            {state.testType === 'full_mock' ? (
              <span className="text-destructive">*</span>
            ) : (
              <span className="text-muted-foreground text-[10px] font-normal">(optional)</span>
            )}
          </Label>
          <Select
            value={state.exam_id ? String(state.exam_id) : '__none__'}
            onValueChange={(val) => handleExamChange(val)}
          >
            <SelectTrigger
              id="exam-select"
              className="w-full h-auto min-h-11 py-2 px-3.5 border-border/80 rounded-xl bg-card text-foreground font-medium text-sm shadow-xs focus:ring-2 focus:ring-primary text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words"
            >
              <SelectValue placeholder="-- Select Target Exam --" />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-border bg-popover text-foreground shadow-glass-md z-50 max-h-72 w-[var(--radix-select-trigger-width)] min-w-[260px] max-w-[92vw]">
              <SelectItem value="__none__" className="text-muted-foreground whitespace-normal break-words py-2">
                -- Select Target Exam --
              </SelectItem>
              {exams.map(exam => (
                <SelectItem key={exam.id} value={String(exam.id)} className="cursor-pointer py-2.5 whitespace-normal break-words">
                  <div className="flex flex-col text-left">
                    <span className="font-semibold text-foreground text-xs sm:text-sm break-words leading-tight">
                      {exam.name}
                    </span>
                    <span className="text-muted-foreground text-[11px] font-mono mt-0.5">
                      {exam.code}{exam.year ? ` • ${exam.year}` : ''}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {state.testType === 'full_mock'
              ? 'Selecting an exam identifies the official blueprint pattern, duration, and question taxonomy.'
              : 'Linking an exam is optional for this test type. It enables exam-specific marking rules if selected.'
            }
          </p>
        </div>

        {/* Total Questions Counter */}
        {state.exam_id && (
          <Card className="bg-primary/5 border-primary/20 flex items-center justify-between p-4 rounded-2xl shadow-glass-xs">
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Approved & Ready Question Pool
              </span>
              <div className="text-3xl font-extrabold text-primary mt-1">
                {totalReadyQuestions.toLocaleString()} Qs
              </div>
              {totalExcludedQuestions > 0 && (
                <div className="flex items-center gap-1.5 text-xs text-tertiary mt-1 font-medium">
                  <AlertCircle className="h-3.5 w-3.5" />
                  <span>{totalExcludedQuestions.toLocaleString()} unapproved / draft questions excluded</span>
                </div>
              )}
            </div>
            <div className="p-3 bg-primary/10 rounded-2xl text-primary">
              <Database className="h-7 w-7" />
            </div>
          </Card>
        )}
      </div>

      {/* Section Template Picker */}
      {state.exam_id && (() => {
        const selectedExam = exams.find(e => e.id === state.exam_id)
        const templates = selectedExam?.section_templates || []
        if (templates.length === 0) return null

        const handleToggleTemplate = (tpl: any) => {
          const alreadySelected = state.blueprintSections.some((bs: any) => bs.id === tpl.id && bs._selected)
          const updated = alreadySelected
            ? state.blueprintSections.map((bs: any) => bs.id === tpl.id ? { ...bs, _selected: false } : bs)
            : state.blueprintSections.map((bs: any) => bs.id === tpl.id ? { ...bs, _selected: true } : bs)

          const selectedTpls = updated.filter((bs: any) => bs._selected)
          const totalQ = selectedTpls.reduce((sum: number, bs: any) => sum + (bs.total_questions || 0), 0)
          const durationM = selectedTpls.reduce((sum: number, bs: any) => sum + (bs.duration_minutes || 0), 0)

          updateState({
            blueprintSections: updated,
            totalQuestions: totalQ > 0 ? totalQ : state.totalQuestions,
            durationMinutes: durationM > 0 ? durationM : state.durationMinutes,
            totalMarks: (totalQ > 0 ? totalQ : state.totalQuestions) * state.markingScheme.positive_marks,
          })
        }

        const selectedSections = state.blueprintSections.filter((bs: any) => bs._selected)

        return (
          <div className="space-y-3 p-4 bg-muted/20 border border-border/70 rounded-2xl">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                <Layers className="h-3.5 w-3.5 text-primary" /> Select Exam Section Templates
              </p>
              <Badge variant="outline" className="text-[10px] font-bold bg-card text-primary border-primary/30">
                {selectedSections.length} / {templates.length} Sections Active
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {templates.map(tpl => {
                const isSelected = state.blueprintSections.some((bs: any) => bs.id === tpl.id && bs._selected)
                return (
                  <div
                    key={tpl.id}
                    onClick={() => handleToggleTemplate(tpl)}
                    className={`p-3.5 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/40 shadow-xs'
                        : 'border-border bg-card hover:border-primary/40'
                    }`}
                  >
                    <div className={`mt-0.5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`}>
                      {isSelected ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-foreground">{tpl.name}</div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        <span className="text-[10px] font-bold text-primary bg-primary/10 px-1.5 py-0.5 rounded-md">
                          {tpl.total_questions} Qs
                        </span>
                        {tpl.duration_minutes && (
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                            {tpl.duration_minutes} min
                          </span>
                        )}
                        {tpl.marking_rule_name && (
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md truncate max-w-[120px]">
                            {tpl.marking_rule_name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            {selectedSections.length > 0 && (
              <p className="text-xs text-primary font-semibold pt-1">
                ✓ {selectedSections.length} section(s) selected — {selectedSections.reduce((s: number, bs: any) => s + (bs.total_questions || 0), 0)} Qs ({selectedSections.reduce((s: number, bs: any) => s + (bs.duration_minutes || 0), 0)} mins total)
              </p>
            )}
          </div>
        )
      })()}

      {/* Chapter Test Mode Hint */}
      {state.testType === 'chapter_test' && (
        <div className="flex items-start gap-3 p-3.5 rounded-xl bg-primary/5 border border-primary/20 text-xs">
          <FileText className="h-4 w-4 text-primary shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-foreground">Chapter Test Mode</p>
            <p className="text-muted-foreground mt-0.5">
              Select your syllabus below, then proceed to <strong>Pool Filters</strong> to isolate specific chapters.
              Default values (60 questions, 90 mins) can be refined in the Weightage step.
            </p>
          </div>
        </div>
      )}

      {/* Loading Indicator */}
      {loading && (
        <div className="flex items-center justify-center py-12 text-muted-foreground gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span>Loading syllabus tree & question pool data...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-4 rounded-md bg-destructive/10 text-destructive text-sm font-medium">
          {error}
        </div>
      )}

      {/* 2. Syllabi Selection List */}
      {!loading && state.syllabiData.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2 text-foreground">
              <BookOpen className="h-5 w-5 text-primary" />
              Mapped Syllabi
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAllSyllabi}
              className="text-xs h-8"
            >
              {state.selectedSyllabiIds.length === state.syllabiData.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {state.syllabiData.map(syl => {
              const isSelected = state.selectedSyllabiIds.includes(syl.id)
              return (
                <div
                  key={syl.id}
                  onClick={() => toggleSyllabus(syl.id)}
                  className={`p-3 rounded-xl border cursor-pointer transition-all flex items-start gap-3 ${
                    isSelected
                      ? 'border-primary bg-primary/5 shadow-xs ring-1 ring-primary/30'
                      : 'border-border hover:bg-muted/40'
                  }`}
                >
                  <div className="mt-0.5 text-primary">
                    {isSelected ? <CheckSquare className="h-5 w-5" /> : <Square className="h-5 w-5 text-muted-foreground" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-xs sm:text-sm break-words text-foreground leading-snug">{syl.name}</div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px] font-mono">
                        v{syl.version}
                      </Badge>
                      <span className="text-xs font-bold text-primary">
                        {syl.total_questions} Ready Qs
                      </span>
                    </div>
                    {syl.excluded_count !== undefined && syl.excluded_count > 0 && (
                      <div className="text-[10px] text-tertiary mt-1 font-medium">
                        ({syl.excluded_count} draft Qs excluded)
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* No Syllabi Found Notice */}
      {!loading && state.exam_id && state.syllabiData.length === 0 && (
        <Card className="p-6 text-center text-muted-foreground rounded-2xl border-border">
          <Info className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <p className="font-medium text-foreground">No active syllabi found for this exam.</p>
          <p className="text-xs mt-1">Please ensure syllabi are mapped to this exam under Admin Taxonomies.</p>
        </Card>
      )}

      {/* 3. Syllabus Tree Breakdown */}
      {!loading && selectedSyllabi.length > 0 && (
        <div className="space-y-4 pt-2">
          <h3 className="text-base sm:text-lg font-semibold flex items-center gap-2 text-foreground">
            <Layers className="h-5 w-5 text-primary" />
            Syllabus Structure & Pool Distribution
          </h3>

          <div className="border border-border rounded-2xl divide-y divide-border bg-card overflow-hidden">
            {selectedSyllabi.map(syl => (
              <div key={syl.id} className="p-3.5 sm:p-5 space-y-3">
                {/* Syllabus Card Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between font-semibold text-sm gap-2 border-b border-border/60 pb-3">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <BookOpen className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span className="break-words font-bold text-foreground leading-snug">{syl.name}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap shrink-0">
                    <Badge variant="default" className="bg-primary text-primary-foreground text-xs font-bold">
                      {syl.total_questions} Approved Qs
                    </Badge>
                    {syl.excluded_count !== undefined && syl.excluded_count > 0 && (
                      <Badge variant="outline" className="text-tertiary border-tertiary/40 bg-tertiary/10 text-xs">
                        {syl.excluded_count} Draft Excluded
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Subjects List */}
                <div className="space-y-2.5">
                  {syl.subjects.map(sub => {
                    const isSubExpanded = expandedSubjects[sub.id]
                    return (
                      <div key={sub.id} className="border border-border/80 rounded-xl bg-card overflow-hidden shadow-2xs">
                        {/* Subject Header */}
                        <div
                          onClick={() => toggleSubject(sub.id)}
                          className="flex items-center justify-between p-2.5 sm:p-3 bg-muted/30 hover:bg-muted/60 cursor-pointer select-none gap-2 transition-colors"
                        >
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            {isSubExpanded ? (
                              <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                            )}
                            <span className="font-semibold text-xs sm:text-sm text-foreground break-words leading-tight">
                              {sub.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Badge variant="outline" className="text-[11px] sm:text-xs bg-background font-semibold text-primary px-2 py-0.5 border-primary/20">
                              {sub.questions_count} Approved Qs
                            </Badge>
                            {sub.excluded_count !== undefined && sub.excluded_count > 0 && (
                              <span className="text-[10px] text-tertiary font-medium hidden sm:inline">
                                ({sub.excluded_count} draft excluded)
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Chapters */}
                        {isSubExpanded && (
                          <div className="p-2.5 space-y-2 border-t border-border/60 bg-muted/10">
                            {sub.chapters.map(chap => {
                              const isChapExpanded = expandedChapters[chap.id]
                              return (
                                <div key={chap.id} className="rounded-xl border border-border/70 bg-card overflow-hidden">
                                  {/* Chapter Header Card */}
                                  <div
                                    onClick={() => toggleChapter(chap.id)}
                                    className="p-2.5 sm:p-3 hover:bg-muted/30 cursor-pointer select-none transition-colors space-y-1.5"
                                  >
                                    <div className="flex items-start justify-between gap-2">
                                      <div className="flex items-start gap-2 min-w-0 flex-1">
                                        {chap.sections.length > 0 ? (
                                          isChapExpanded ? (
                                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                                          ) : (
                                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                                          )
                                        ) : (
                                          <span className="w-3.5 shrink-0" />
                                        )}
                                        <span className="font-medium text-xs sm:text-sm text-foreground break-words leading-snug">
                                          {chap.name}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1 shrink-0 mt-0.5">
                                        <Badge variant="secondary" className="text-[10px] sm:text-[11px] font-semibold bg-primary/10 text-primary border-primary/20 px-1.5 py-0.5">
                                          {chap.questions_count} Qs
                                        </Badge>
                                        {chap.excluded_count !== undefined && chap.excluded_count > 0 && (
                                          <span className="text-[10px] text-tertiary hidden sm:inline font-medium">
                                            ({chap.excluded_count} draft)
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Standards Badges below the title */}
                                    {chap.standards && chap.standards.length > 0 && (
                                      <div className="flex items-center gap-1.5 flex-wrap pl-5.5">
                                        {chap.standards.map(st => (
                                          <Badge key={st.id} variant="outline" className="text-[9px] sm:text-[10px] px-1.5 py-0 h-4 border-border text-muted-foreground font-mono">
                                            {st.name}
                                          </Badge>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  {/* Sections */}
                                  {isChapExpanded && chap.sections.length > 0 && (
                                    <div className="pl-4 sm:pl-7 pr-3 py-2 space-y-1.5 border-t border-border/50 bg-muted/20 text-xs">
                                      {chap.sections.map(sec => (
                                        <div key={sec.id} className="flex items-start justify-between py-1 text-muted-foreground gap-2">
                                          <div className="flex items-start gap-1.5 min-w-0 flex-1">
                                            <FileText className="h-3 w-3 text-muted-foreground shrink-0 mt-0.5" />
                                            <span className="break-words text-[11px] sm:text-xs text-foreground leading-snug">{sec.name}</span>
                                          </div>
                                          <div className="flex items-center gap-1 shrink-0">
                                            <span className="text-[10px] sm:text-[11px] font-semibold text-primary">{sec.questions_count} Ready Qs</span>
                                            {sec.excluded_count !== undefined && sec.excluded_count > 0 && (
                                              <span className="text-[9px] sm:text-[10px] text-tertiary">({sec.excluded_count} draft)</span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
